import { useEffect, useMemo, useRef, useState } from 'react';
import { countryName, sortedCountries } from '../lib/countries';

type Props = {
  value: string;
  onChange: (code: string) => void;
};

export default function CountrySelect({ value, onChange }: Props) {
  const all = useMemo(() => sortedCountries(), []);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (c) =>
        c.code.toLowerCase().startsWith(q) ||
        c.name.toLowerCase().includes(q),
    );
  }, [all, query]);

  const display = value ? `${value} — ${countryName(value)}` : '';

  return (
    <div ref={wrapRef} className="combobox">
      <input
        className="input"
        placeholder="Select country"
        value={open ? query : display}
        onFocus={() => {
          setOpen(true);
          setQuery('');
        }}
        onChange={(e) => {
          setOpen(true);
          setQuery(e.target.value);
        }}
      />
      {open && (
        <div className="combobox-list">
          {filtered.length === 0 ? (
            <div className="combobox-empty">No matches</div>
          ) : (
            filtered.slice(0, 100).map((c) => (
              <button
                key={c.code}
                type="button"
                className={`combobox-item ${c.code === value ? 'active' : ''}`}
                onClick={() => {
                  onChange(c.code);
                  setOpen(false);
                  setQuery('');
                }}
              >
                <span className="code">{c.code}</span>
                <span className="name">{c.name}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
